import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, RefreshCw, Building2, Link2, Download } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import BokunCuentas from "@/pages/config/bokun/BokunCuentas";
import BokunMapeo from "@/pages/config/bokun/BokunMapeo";
import BokunImporter from "@/pages/config/bokun/BokunImporter";

// PROMPT 140: si la plataforma cierra y abre fechas en Bókun. Apagado (el default) no toca nada
// en Bókun: operaciones pidió "desligar" la disponibilidad mientras cuadra la plataforma con el Drive.
function DisponibilidadBokun() {
  const { isAdmin } = useAuth();
  const [conectada, setConectada] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => setConectada(!!data.bokun_disponibilidad_conectada))
      .catch(() => setConectada(null));
  }, []);

  const cambiar = async (valor) => {
    setGuardando(true);
    try {
      const { data } = await api.put("/settings", { bokun_disponibilidad_conectada: valor });
      setConectada(!!data.bokun_disponibilidad_conectada);
      toast.success(valor
        ? "Listo: la plataforma cierra y abre fechas en Bókun."
        : "Listo: la plataforma ya no toca la disponibilidad de Bókun.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Card className="p-4" data-testid="bokun-disponibilidad">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">Cerrar y abrir fechas en Bókun desde la plataforma</p>
          {conectada === null ? (
            <p className="text-[12px] text-slate-400 mt-1">Cargando…</p>
          ) : conectada ? (
            <p className="text-[12px] text-slate-600 mt-1" data-testid="bokun-disponibilidad-texto">
              <strong>Prendido.</strong> Cuando una salida se llena, la plataforma cierra ese horario en Bókun y lo
              vuelve a abrir si se libera lugar; “Cerrar salida” y “Abrir” también cierran y abren en Bókun. No cierra
              si el mapeo no tiene el horario y ese día el tour sale a más de una hora, ni si a esa hora otra salida
              del tour todavía tiene lugar: en esos casos el panel de la salida avisa que hay que cerrarlo a mano. Lo
              que ya está lleno al prenderlo no se cierra solo.
            </p>
          ) : (
            <p className="text-[12px] text-slate-600 mt-1" data-testid="bokun-disponibilidad-texto">
              <strong>Apagado.</strong> La plataforma no cierra ni abre nada en Bókun: ni sola cuando una salida se
              llena, ni con “Cerrar salida”. Las fechas se cierran a mano en Bókun.
            </p>
          )}
          {!isAdmin && <p className="text-[11px] text-slate-400 mt-1">Solo un administrador lo puede cambiar.</p>}
        </div>
        <Switch
          checked={!!conectada}
          onCheckedChange={cambiar}
          disabled={!isAdmin || guardando || conectada === null}
          data-testid="bokun-disponibilidad-switch"
        />
      </div>
    </Card>
  );
}

// PROMPT 153: saldo de las ventas de las webs propias (Interviajes NY, Vacaciones Nueva
// York, Intertravel NYC y Excursiones Nueva York) según Bókun. Entra apagado: con el
// interruptor apagado la sincronización solo anota lo que cambiaría y acá se ve.
const fechaCorta = (f) => (f ? String(f).split("-").reverse().join("/") : "—");
const dineroTxt = (v, moneda) => `${Number(v || 0).toFixed(2)} ${moneda || "USD"}`;
const importesTxt = (d, moneda) =>
  d ? `Pagado ${dineroTxt(d.monto_pagado, moneda)} · Saldo ${dineroTxt(d.saldo_pendiente, moneda)}` : "—";

function hoyTxt(f) {
  const a = f.antes || {};
  if (a.facturada) return `Facturada · ${dineroTxt(a.monto_facturado_agencia, f.moneda)} facturado`;
  return importesTxt(a, f.moneda);
}

function bokunTxt(f) {
  if (f.estado_pago === "PAID_IN_FULL") return "Pagado completo";
  const d = f.despues || {};
  const total = Number(d.monto_pagado || 0) + Number(d.saldo_pendiente || 0);
  if (f.saldo_manual || total <= 0) return "Depósito";
  return `Depósito ${Math.round((Number(d.monto_pagado || 0) / total) * 100)} %`;
}

function resumenSaldoDirectas(d) {
  const t = d.por_tipo || {};
  return (
    `${d.total} reserva${d.total === 1 ? "" : "s"} de hoy en adelante: ` +
    `${t.aparece_saldo || 0} pasan a mostrar su saldo, ` +
    `${t.desaparece_saldo || 0} dejan de mostrar un saldo que ya pagaron, ` +
    `${t.cambia_saldo || 0} cambian de saldo y ` +
    `${t.solo_facturada || 0} solo dejan de figurar como Facturada`
  );
}

function sumasTxt(m) {
  const partes = Object.entries(m || {}).map(([moneda, v]) => dineroTxt(v, moneda));
  return partes.length ? partes.join(" · ") : "0.00";
}

function SaldoVentasDirectas({ refreshKey }) {
  const { isAdmin } = useAuth();
  const [activo, setActivo] = useState(null);
  const [datos, setDatos] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [pendiente, setPendiente] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setActivo(!!data.bokun_saldo_ventas_directas);
    } catch (e) {
      setActivo(null);
    }
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/bokun/saldo-ventas-directas");
      setDatos(data);
    } catch (e) {
      setDatos(null);
    }
  }, [isAdmin]);

  useEffect(() => {
    cargar();
  }, [cargar, refreshKey]);

  const cambiar = async (valor) => {
    setGuardando(true);
    try {
      const { data } = await api.put("/settings", { bokun_saldo_ventas_directas: valor });
      setActivo(!!data.bokun_saldo_ventas_directas);
      toast.success(valor
        ? "Listo: desde la próxima sincronización el saldo de las webs propias sale de Bókun."
        : "Listo: desde la próxima sincronización vuelve el cálculo anterior.");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
      setPendiente(null);
    }
  };

  const total = datos?.total ?? 0;
  const items = datos?.items || [];

  return (
    <Card className="p-4" data-testid="bokun-saldo-directas">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">Saldo de las ventas de las webs propias</p>
          {activo === null ? (
            <p className="text-[12px] text-slate-400 mt-1">Cargando…</p>
          ) : activo ? (
            <p className="text-[12px] text-slate-600 mt-1" data-testid="bokun-saldo-directas-texto">
              <strong>Prendido.</strong> En las ventas de las webs propias el saldo sale de Bókun: depósito o
              pagado completo, con el descuento del código promocional y repartido entre las actividades del
              mismo booking. Nunca figuran como Facturada. Las reservas con el saldo ajustado a mano no se
              tocan.
            </p>
          ) : (
            <p className="text-[12px] text-slate-600 mt-1" data-testid="bokun-saldo-directas-texto">
              <strong>Apagado.</strong> Las ventas de las webs propias (Interviajes NY, Vacaciones Nueva York,
              Intertravel NYC y Excursiones Nueva York) siguen como hasta ahora. La sincronización anota lo que
              cambiaría al prenderlo.
            </p>
          )}
          {!isAdmin && <p className="text-[11px] text-slate-400 mt-1">Solo un administrador lo puede cambiar.</p>}
        </div>
        <Switch
          checked={!!activo}
          onCheckedChange={(v) => setPendiente(!!v)}
          disabled={!isAdmin || guardando || activo === null}
          data-testid="bokun-saldo-directas-switch"
        />
      </div>

      {isAdmin && datos && (
        <div className="mt-3" data-testid="bokun-saldo-directas-resumen">
          {total === 0 ? (
            <p className="text-[12px] text-slate-500">
              {activo
                ? "No falta aplicar nada."
                : "No hay nada para cambiar todavía. Usá Sincronizar todo para revisar todas las fechas."}
            </p>
          ) : (
            <>
              <p className="text-[12px] text-slate-700">
                {activo ? "Falta aplicar (baja a 0 después de la sincronización): " : ""}
                {resumenSaldoDirectas(datos)}.
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                Saldo que se ve hoy: {sumasTxt(datos.saldo_hoy)} · con el cálculo nuevo: {sumasTxt(datos.saldo_nuevo)}
                {total > items.length ? ` · Se muestran las primeras ${items.length}.` : ""}
              </p>
              <div className="mt-2 max-h-80 overflow-auto border border-slate-200 rounded-md">
                <table className="w-full text-[12px]" data-testid="bokun-saldo-directas-tabla">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="px-2 py-1.5 font-medium">Código</th>
                      <th className="px-2 py-1.5 font-medium">Fecha</th>
                      <th className="px-2 py-1.5 font-medium">Cuenta</th>
                      <th className="px-2 py-1.5 font-medium">Hoy</th>
                      <th className="px-2 py-1.5 font-medium">Con el cálculo nuevo</th>
                      <th className="px-2 py-1.5 font-medium">Bókun</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((f) => (
                      <tr
                        key={f.reserva_id}
                        className="border-t border-slate-100"
                        data-testid={`bokun-saldo-directas-fila-${f.codigo_booking}`}
                      >
                        <td className="px-2 py-1.5 font-mono">{f.codigo_booking}</td>
                        <td className="px-2 py-1.5">{fechaCorta(f.fecha)}</td>
                        <td className="px-2 py-1.5">{f.cuenta || "—"}</td>
                        <td className="px-2 py-1.5">{hoyTxt(f)}</td>
                        <td className="px-2 py-1.5">
                          {f.saldo_manual
                            ? "Ajustada a mano: solo deja de figurar como Facturada"
                            : importesTxt(f.despues, f.moneda)}
                        </td>
                        <td className="px-2 py-1.5">{bokunTxt(f)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <AlertDialog open={pendiente !== null} onOpenChange={(abierto) => { if (!abierto) setPendiente(null); }}>
        <AlertDialogContent data-testid="bokun-saldo-directas-confirmar">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendiente ? "Prender el saldo de las ventas de las webs propias" : "Apagar el saldo de las ventas de las webs propias"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendiente
                ? `Se corrigen ${total} reservas en la próxima sincronización (cada 15 minutos, o ya con Sincronizar todo). En la auditoría queda cada cambio con su valor anterior.`
                : "Desde la próxima sincronización vuelve el cálculo anterior (sin el descuento ni el reparto entre actividades). Las reservas que ya cambiaron no vuelven a figurar como Facturada."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bokun-saldo-directas-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cambiar(pendiente)}
              data-testid="bokun-saldo-directas-aceptar"
            >
              {pendiente ? "Prender" : "Apagar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function BokunConfig() {
  const [estado, setEstado] = useState(null);
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("cuentas");
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(null);

  const syncing = job?.estado === "en_progreso";

  const loadEstado = useCallback(async () => {
    try {
      const { data } = await api.get("/bokun/estado");
      setEstado(data);
    } catch (err) {
      /* noop */
    }
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollOnce = useCallback(async () => {
    try {
      const { data } = await api.get("/bokun/sync/estado");
      setJob(data);
      if (data.estado !== "en_progreso") {
        stopPolling();
        if (data.estado === "completado") {
          const r = data.resumen || {};
          if ((r.cuentas_con_error || 0) > 0) {
            toast.warning(`Sincronización con avisos: ${r.cuentas_con_error} cuenta(s) con error. ${r.nuevas || 0} nuevas, ${r.actualizadas || 0} actualizadas.`);
          } else {
            toast.success(`Sincronización lista: ${r.nuevas || 0} nuevas, ${r.actualizadas || 0} actualizadas, ${r.a_revisar || 0} a revisar.`);
          }
        } else if (data.estado === "error") {
          toast.error(`Error de sincronización: ${data.error || "desconocido"}`);
        }
        loadEstado();
        setRefreshKey((k) => k + 1);
      }
    } catch (e) {
      /* noop */
    }
  }, [loadEstado]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollOnce();
    pollRef.current = setInterval(pollOnce, 2000);
  }, [pollOnce]);

  useEffect(() => {
    loadEstado();
    (async () => {
      try {
        const { data } = await api.get("/bokun/sync/estado");
        setJob(data);
        if (data.estado === "en_progreso") startPolling();
      } catch (e) {
        /* noop */
      }
    })();
    return stopPolling;
  }, [loadEstado, startPolling]);

  const syncAll = async () => {
    try {
      const { data } = await api.post("/bokun/sync-now", {});
      if (data.estado === "sin_cuentas") {
        toast.info("No hay cuentas activas con credenciales cargadas.");
        return;
      }
      toast.info("Sincronización iniciada en segundo plano…");
      setJob({ estado: "en_progreso", progreso: { cuentas_total: data.cuentas || 0, cuentas_procesadas: 0 } });
      startPolling();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const p = job?.progreso || {};

  return (
    <div className="space-y-4" data-testid="bokun-config">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
          {estado && (
            <>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-100" data-testid="bokun-contador-activas">
                <Building2 className="h-3.5 w-3.5" /> {estado.cuentas_activas}/{estado.cuentas_total} cuentas activas
              </span>
              {estado.productos_sin_mapear > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50 text-amber-700">
                  {estado.productos_sin_mapear} producto(s) sin mapear
                </span>
              )}
            </>
          )}
        </div>
        <Button onClick={syncAll} disabled={syncing} data-testid="bokun-sync-all-button" className="gap-2">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {syncing ? "Sincronizando…" : "Sincronizar todo"}
        </Button>
      </div>

      {syncing && (
        <Card className="p-3 bg-blue-50 border-blue-200" data-testid="bokun-sync-progress">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            <span>
              Sincronizando{p.cuenta_actual ? ` cuenta “${p.cuenta_actual}”` : "…"}
              {" "}({p.cuentas_procesadas || 0}/{p.cuentas_total || 0} cuentas
              {p.total_cuenta ? ` · ${p.procesadas_cuenta || 0}/${p.total_cuenta} reservas` : ""}) ·{" "}
              {p.nuevas || 0} nuevas, {p.actualizadas || 0} actualizadas, {p.a_revisar || 0} a revisar
            </span>
          </div>
        </Card>
      )}

      <DisponibilidadBokun />
      <SaldoVentasDirectas refreshKey={refreshKey} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="cuentas" data-testid="bokun-tab-cuentas" className="gap-2">
            <Building2 className="h-4 w-4" /> Cuentas
          </TabsTrigger>
          <TabsTrigger value="mapeo" data-testid="bokun-tab-mapeo" className="gap-2">
            <Link2 className="h-4 w-4" /> Mapeo de productos
          </TabsTrigger>
          <TabsTrigger value="importar" data-testid="bokun-tab-importar" className="gap-2">
            <Download className="h-4 w-4" /> Importar histórico
          </TabsTrigger>
        </TabsList>
        <TabsContent value="cuentas" className="mt-5">
          <BokunCuentas
            onChanged={loadEstado}
            onSyncStarted={startPolling}
            syncing={syncing}
            refreshKey={refreshKey}
          />
        </TabsContent>
        <TabsContent value="mapeo" className="mt-5">
          <BokunMapeo />
        </TabsContent>
        <TabsContent value="importar" className="mt-5">
          <BokunImporter />
        </TabsContent>
      </Tabs>
    </div>
  );
}
